import type {
	RecommendationContext,
	RecommendationContextAxis,
	RecommendationContextCell,
} from "@memorang/types";
import type { ProficiencyBandFill } from "./language-learning";

export type MasteryWindow = 10 | 20 | 30;

export type MasteryScope = { axisId: string; valueId: number };

export type MasteryLevelSummary = {
	label: string;
	mastery: number | null;
	mastered: boolean;
	totalResponses: number;
};

export const SKILL_MASTERY_WINDOW: MasteryWindow = 20;
export const QUESTION_TYPE_MASTERY_WINDOW: MasteryWindow = 10;

const getProgressionAxis = (
	context: RecommendationContext,
): RecommendationContextAxis | undefined =>
	context.axes.find(({ role }) => role === "PROGRESSION");

const orderedValues = (axis: RecommendationContextAxis) =>
	[...axis.values].sort(
		(left, right) => left.sortOrder - right.sortOrder || left.id - right.id,
	);

const rollingMasteryAt = (
	cell: RecommendationContextCell,
	masteryWindow: MasteryWindow,
): number | null => cell.evidence[`rollingMastery${masteryWindow}`];

const findPairCell = (
	context: RecommendationContext,
	scope: MasteryScope,
	progressionAxisId: string,
	levelId: number,
): RecommendationContextCell | undefined =>
	context.cells.find(
		(cell) =>
			cell.coordinates.length === 2 &&
			cell.coordinates.some(
				({ axisId, valueId }) =>
					axisId === scope.axisId && valueId === scope.valueId,
			) &&
			cell.coordinates.some(
				({ axisId, valueId }) =>
					axisId === progressionAxisId && valueId === levelId,
			),
	);

/**
 * The unique axis whose values contain `valueId`, as a scope for the fill and
 * estimate builders. Undefined when no axis (or more than one) matches, so an
 * ambiguous id can never silently pick a wrong axis.
 */
export const findScopeForValue = (
	context: RecommendationContext,
	valueId: number,
): MasteryScope | undefined => {
	const matches = context.axes.filter((axis) =>
		axis.values.some(({ id }) => id === valueId),
	);
	return matches.length === 1 ? { axisId: matches[0].id, valueId } : undefined;
};

// Unmastered fills cap at 99 so "ring complete ⇔ mastered" survives rounding
// (e.g. 89.6/90 rounds to 100 but is not mastered).
const toFillPercent = (
	mastery: number | null,
	passingMastery: number,
): number => {
	if (mastery === null) {
		return 0;
	}
	if (mastery >= passingMastery) {
		return 100;
	}
	return Math.min(99, Math.round((mastery / passingMastery) * 100));
};

type MasteryArgs = {
	context: RecommendationContext;
	scope: MasteryScope;
	masteryWindow: MasteryWindow;
};

type ProgressionValue = RecommendationContextAxis["values"][number];

type MasteryLevelCell = {
	value: ProgressionValue;
	cell: RecommendationContextCell | undefined;
	mastery: number | null;
};

const collectMasteryLevelCells = ({
	context,
	scope,
	masteryWindow,
}: MasteryArgs): MasteryLevelCell[] => {
	const progression = getProgressionAxis(context);
	if (!progression || progression.id === scope.axisId) {
		return [];
	}
	return orderedValues(progression).map((value) => {
		const cell = findPairCell(context, scope, progression.id, value.id);
		return {
			value,
			cell,
			mastery: cell ? rollingMasteryAt(cell, masteryWindow) : null,
		};
	});
};

export const buildMasteryLevelSummaries = (
	args: MasteryArgs,
): MasteryLevelSummary[] => {
	const { passingMastery } = args.context.policy;
	return collectMasteryLevelCells(args).map(({ value, cell, mastery }) => ({
		label: value.title,
		mastery,
		mastered: mastery !== null && mastery >= passingMastery,
		totalResponses: cell?.evidence.totalResponses ?? 0,
	}));
};

export const buildMasteryBandFills = (
	args: MasteryArgs,
): ProficiencyBandFill[] => {
	const { passingMastery } = args.context.policy;
	return collectMasteryLevelCells(args).map(({ value, cell, mastery }) => ({
		label: value.title,
		percent: toFillPercent(mastery, passingMastery),
		hasItems: (cell?.evidence.totalResponses ?? 0) > 0,
		hasAvailableItems: (cell?.launch.availableItemCount ?? 0) > 0,
	}));
};

export const estimateProficiencyLevel = (
	args: MasteryArgs,
): string | undefined => {
	const levelsLowToHigh = buildMasteryLevelSummaries(args);
	const highestMasteredLevel = [...levelsLowToHigh]
		.reverse()
		.find((level) => level.mastered);
	return highestMasteredLevel?.label;
};

export const resolveMasteryForTagId = (
	context: RecommendationContext | undefined,
	tagId: number,
	masteryWindow: MasteryWindow,
	fallbackLevelLabel: string | undefined,
): {
	fills: ProficiencyBandFill[];
	estimatedLevel: string | undefined;
	displayLevel: string | undefined;
} => {
	const scope = context ? findScopeForValue(context, tagId) : undefined;
	if (!context || !scope) {
		return {
			fills: [],
			estimatedLevel: undefined,
			displayLevel: fallbackLevelLabel,
		};
	}
	const fills = buildMasteryBandFills({ context, scope, masteryWindow });
	const estimatedLevel = estimateProficiencyLevel({
		context,
		scope,
		masteryWindow,
	});
	return {
		fills,
		estimatedLevel,
		displayLevel: estimatedLevel ?? fallbackLevelLabel ?? fills[0]?.label,
	};
};
